import {
    Component,
    computed,
    effect,
    input,
    output,
    signal,
    ChangeDetectionStrategy,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ContactRoleData } from "../data.ingestion.service";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";

@TypeManagerDecorator("s25-ng-contact-role-field")
@Component({
    selector: "s25-ng-contact-role-field",
    standalone: true,
    imports: [FormsModule, S25DropdownPaginatedModule],
    template: `
        <div class="contact-role-field">
            @for (row of rows(); track $index) {
                <div class="contact-role-row">
                    <div class="contact-role-role">
                        <s25-ng-dropdown-search-criteria
                            [type]="'eventRoles'"
                            [value]="row.roleItem"
                            (valueChange)="onRoleChange($index, $event)"
                            [readOnly]="readOnly()"
                            [placeholder]="'Select Role'"
                        />
                    </div>
                    <div class="contact-role-contact">
                        <s25-ng-dropdown-search-criteria
                            [type]="'contacts'"
                            [value]="row.contactItem"
                            (valueChange)="onContactChange($index, $event)"
                            [readOnly]="readOnly()"
                            [placeholder]="'Select Contact'"
                        />
                    </div>
                    @if (!readOnly()) {
                        <button type="button" class="btn-remove" (click)="removeRow($index)" title="Remove">
                            &times;
                        </button>
                    }
                </div>
            }
            @if (!readOnly() && canAddRow()) {
                <button type="button" class="btn-add" (click)="addRow()">Add Role</button>
            }
        </div>
    `,
    styles: `
        .contact-role-field {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .contact-role-row {
            display: flex;
            gap: 8px;
            align-items: center;
        }

        .contact-role-role,
        .contact-role-contact {
            flex: 1;
            min-width: 0;
        }

        .btn-remove {
            flex-shrink: 0;
            width: 32px;
            height: 34px;
            padding: 0;
            border: 1px solid #ccc;
            border-radius: 4px;
            background: #f8f9fa;
            font-size: 18px;
            line-height: 1;
            cursor: pointer;
            color: #666;
        }

        .btn-add {
            margin-top: 4px;
            padding: 6px 12px;
            border: 1px solid #2573a7;
            border-radius: 4px;
            background: #fff;
            color: #2573a7;
            font-size: 14px;
            cursor: pointer;
            align-self: flex-start;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactRoleFieldComponent {
    readonly value = input<ContactRoleData>();
    readonly readOnly = input(false);
    readonly valueChange = output<ContactRoleData>();

    readonly rows = signal<{ roleItem?: DropDownItem; contactItem?: DropDownItem }[]>([]);
    private initialized = false;

    // Only allow adding a new row when all existing rows are complete (have both role and contact)
    readonly canAddRow = computed(() => this.rows().every((r) => r.roleItem?.itemId && r.contactItem?.itemId));

    constructor() {
        effect(() => {
            const val = this.value();
            if (this.initialized) return;
            this.initialized = true;
            if (val?.items?.length) {
                this.rows.set(
                    val.items.map((item) => ({
                        roleItem: item.roleId ? { itemId: item.roleId, itemName: item.roleName || "" } : undefined,
                        contactItem: item.contactId
                            ? { itemId: item.contactId, itemName: item.contactName || "" }
                            : undefined,
                    })),
                );
            } else {
                this.rows.set([{}]);
            }
        });
    }

    addRow() {
        this.rows.update((r) => [...r, {}]);
    }

    removeRow(index: number) {
        this.rows.update((r) => r.filter((_, i) => i !== index));
        this.emit();
    }

    onRoleChange(index: number, item: DropDownItem | undefined) {
        // Disallow duplicate roles
        if (item?.itemId && this.isRoleTaken(item.itemId, index)) {
            return;
        }
        this.rows.update((r) => r.map((row, i) => (i === index ? { ...row, roleItem: item || undefined } : row)));
        this.emitIfRowComplete(index);
    }

    onContactChange(index: number, item: DropDownItem | undefined) {
        this.rows.update((r) => r.map((row, i) => (i === index ? { ...row, contactItem: item || undefined } : row)));
        this.emitIfRowComplete(index);
    }

    // Only emit when the changed row has both a role and a contact
    private emitIfRowComplete(index: number) {
        const row = this.rows()[index];
        if (row?.roleItem?.itemId && row?.contactItem?.itemId) {
            this.emit();
        }
    }

    private isRoleTaken(roleId: number | string, excludeIndex: number): boolean {
        return this.rows().some((row, i) => i !== excludeIndex && row.roleItem?.itemId === roleId);
    }

    private emit() {
        const items = this.rows()
            .filter((r) => r.roleItem?.itemId && r.contactItem?.itemId)
            .map((r) => ({
                roleId: Number(r.roleItem!.itemId),
                roleName: r.roleItem!.itemName,
                contactId: Number(r.contactItem!.itemId),
                contactName: r.contactItem!.itemName,
            }));
        this.valueChange.emit({ items });
    }
}
